import {
  CTA_BUTTON_COPY,
  CTA_BUTTON_COPY_8,
  CTA_BUTTON_COPY_9,
  CTA_LINK_2,
  FORM_IDS,
} from "components/data/landing-page-data";
import CaseStudiesF from "components/landing-pages/surveillance/version-f/CaseStudiesF";
import FormSectionF from "components/landing-pages/surveillance/version-f/FormSectionF";
import HighlightsF from "components/landing-pages/surveillance/version-f/HighlightsF";
import PlatformF from "components/landing-pages/surveillance/version-f/PlatformF";
import GlobalLayout from "components/page/GlobalLayout";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

import icon1 from "components/landing-pages/icons/bandwidth.svg";
import icon2 from "components/landing-pages/icons/handyman.svg";
import icon3 from "components/landing-pages/icons/system.svg";
import icon4 from "components/landing-pages/icons/security.svg";
import HeroVersionG from "components/landing-pages/experiments/HeroVersionG";
import AiBanner from "components/common/banners/AiBanner";

const EXPERIMENTAL_FOOTER_MENUS = [
  {
    title: "LEARN MORE",
    links: [
      { title: "Pricing", path: "/lp/pricing/" },
      { title: "Contact", path: "/lp/cta-3/" },
    ],
  },
  {
    title: "PRODUCTS",
    links: [
      { title: "Cameras", path: "/lp/cameras-f/" },
      { title: "Access Control", path: "/lp/access-control-d/" },
    ],
  },
];

const SecurityUkLandingPage = () => {
  const imageData = useStaticQuery(graphql`
    query {
      hero: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/security-g-hero.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      logo1: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/pepsico-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 43, width: 168)
        }
      }
      logo2: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/marshall-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 50, width: 133)
        }
      }
      logo3: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/aramark-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 42, width: 148)
        }
      }
      logo4: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/techspace-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 37, width: 168)
        }
      }
      logo5: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/lad-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 52, width: 72)
        }
      }
      logo6: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/trainline-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 38, width: 167)
        }
      }
      card1: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/sec-g-platform-card1-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card2: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/sec-g-platform-card2-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card3: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/sec-g-platform-card3-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card4: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/sec-g-platform-card4-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudy1: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/metasla.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo1: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/metasla-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudy2: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/blakes.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo2: file(
        relativePath: { eq: "components/common/logos/color/blakes-logo.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudy3: file(
        relativePath: {
          eq: "components/landing-pages/surveillance/version-f/assets/middleby.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo3: file(
        relativePath: { eq: "components/common/logos/color/middleby-logo.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  return (
    <GlobalLayout
      landingPage
      useExperimentalFooter
      includeButton
      buttonText={CTA_BUTTON_COPY}
      color="var(--secondary-bg)"
      expirementalFooterMenus={EXPERIMENTAL_FOOTER_MENUS}
    >
      <Helmet>
        <title>Rhombus Cameras</title>
        <meta
          name="description"
          content="Discover the power of cloud-managed security cameras."
        />
      </Helmet>
      <HeroVersionG
        imageEnd={false}
        imageData={imageData.hero}
        title={"#1 Security Monitoring Platform for Safer, Smarter Spaces"}
        blurb={
          "Protect people and assets with a full suite of cloud-based physical security solutions that are powered by AI insights, remote management, and real-time alerts."
        }
        logos={[
          imageData.logo1,
          imageData.logo2,
          imageData.logo3,
          imageData.logo4,
          imageData.logo5,
          imageData.logo6,
        ]}
      />
      <HighlightsF
        stats={[
          { title: "99.9%", subTitle: "Platform Uptime for Unlimited Devices" },
          { title: "120K+", subTitle: "Cameras Deployed Globally" },
          { title: "3M+", subTitle: "Video Events Analysed Daily" },
        ]}
        iconBoxes={[
          {
            icon: { src: icon1 },
            text: "High bandwidth consumption that overwhelms your network",
          },
          {
            icon: { src: icon2 },

            text: "Complex installs, unnecessary hardware, and added costs",
          },
          {
            icon: { src: icon3 },

            text: "Device and connectivity issues leaving critical operations blind",
          },
          {
            icon: { src: icon4 },

            text: "Cybersecurity and port-forwarding risks",
          },
        ]}
        title="Security Monitoring Systems Shouldn't Be Complex"
      />
      <PlatformF
        title="One Physical Security Platform. Complete Control."
        bgColor="var(--neutral-100)"
        cardColor="var(--white)"
        cardBgColor="var(--primary-bg)"
        showCta
        ctaData={{ title: CTA_BUTTON_COPY_9, link: CTA_LINK_2 }}
        cards={[
          {
            image: imageData.card1,
            title: "Integrated Physical Security",
            blurb:
              "Suite of cameras, access control, IoT sensors and alarm monitoring on a secure, remote dashboard and mobile app",
          },
          {
            image: imageData.card2,
            title: "Rapid, AI-Powered Search",
            blurb:
              "Smarter, faster investigations with AI video search and intelligent filtering based on faces, number plates, colors and regions.",
          },
          {
            image: imageData.card3,
            title: "Rapid Deterrence and Response",
            blurb:
              "Automate audio and visual deterrence. Leverage TMA 5 Diamond certified live agents for threat verification and dispatch.",
          },
          {
            image: imageData.card4,
            title: "Reliable and Scalable System",
            blurb:
              "PoE cloud-connected cameras with onboard storage and single cable install. Integrate existing 3P cameras on a single platform.",
          },
        ]}
      />
      <AiBanner />
      <CaseStudiesF
        caseStudies={[
          {
            image: imageData.caseStudy1,
            stats: [
              { title: "80", subTitle: "hours saved weekly" },
              { title: "65,000", subTitle: "square metres" },
            ],
            quote: (
              <>
                For every camera we've had,{" "}
                <b>Rhombus saves us thousands of dollars</b> each month in terms
                of diagnosing issues preemptively, catching things before they
                go wrong, and preventing downtime.
              </>
            ),
            logo: { src: imageData.caseStudyLogo1, height: 36, width: 132 },
          },
          {
            image: imageData.caseStudy2,
            stats: [
              { title: "99%", subTitle: "Faster Searches" },
              { title: "60+", subTitle: "Locations" },
            ],
            quote: (
              <>
                <b>
                  The quality and speed of service we get from Rhombus are
                  incredible
                </b>
                . Installation is simple and takes fewer people than before. If
                there's a problem with a camera, I get an alert before I even
                know there's an issue
              </>
            ),
            logo: { src: imageData.caseStudyLogo2, height: 32, width: 116 },
          },
          {
            image: imageData.caseStudy3,
            stats: [
              { title: "80%", subTitle: "time savings" },
              { title: "200+", subTitle: "employees" },
            ],
            quote: (
              <>
                With legacy systems, investigating incidents took me days of
                manually scrolling through footage.{" "}
                <b>With Rhombus, I can do it all in 10 minutes</b>—quickly
                finding the footage and securely sharing clips via text or
                email.
              </>
            ),
            logo: { src: imageData.caseStudyLogo3, height: 22, width: 143 },
          },
        ]}
      />
      <FormSectionF
        formTitle={"Get a Quote"}
        formSubmitText={CTA_BUTTON_COPY_8}
        formId={FORM_IDS.GET_QUOTE_ADS}
        checkList={["Custom platform demo", "Customised free trial", "Pricing"]}
        subTitle="Modernise Your Security Infrastructure"
      />
    </GlobalLayout>
  );
};

export default SecurityUkLandingPage;
